/**
 * Sidebar - branding + RoleSwitcher + role-aware navigation.
 *
 * Top: tenant branding block (logo + name + address from useTenantData).
 * Below branding: RoleSwitcher (per role-pattern.md § Sidebar template).
 * Body: role-aware nav links via React Router NavLink.
 *
 * Per CHAP-78 (Phase 1C #44).
 */
import { NavLink, useNavigate } from 'react-router-dom';
import { useFeatureFlagBoolean } from '../../hooks/useFeatureFlag.js';
import { useTenantData } from '../../hooks/useTenantData.js';
import { useUserRoles } from '../../hooks/useUserRoles.js';
import { useLaggingConversations } from '../../hooks/useLaggingConversations.js';
import { useTheme } from '../../hooks/useTheme.js';
import { RoleSwitcher } from './RoleSwitcher.js';
import { setStoredRole } from '../../lib/active-role.js';
import { apiPost } from '../../lib/api.js';
import type { Role } from '../../../shared/types.js';

async function signOut() {
  try {
    await apiPost('/api/auth/logout');
  } catch {
    // ignore - clear client state regardless
  }
  // Full reload to /login clears the React Query cache + re-evaluates auth.
  window.location.href = '/login';
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType;
  end?: boolean;
  /** Count chip on the right edge of the item (rendered when > 0). */
  badge?: number;
}

interface SidebarProps {
  pathname: string;
  tenantSlug?: string;
  onNavigate?: () => void;
  activeRole: Role;
}

export function Sidebar({ pathname, tenantSlug, onNavigate, activeRole }: SidebarProps) {
  const navigate = useNavigate();
  const { data: tenant } = useTenantData(tenantSlug ?? 'omega-zeta');
  const roles = useUserRoles();
  const { theme, cycleTheme } = useTheme();

  // CHAP-302: lagging-conversations count badge on the fundraising nav item
  // (Analytics IS the fundraising entry - /analytics redirects to
  // /analytics/fundraising). The hook self-disables below cabinet, so the
  // badge is cabinet+ only per the AC; count 0 renders nothing.
  const lagging = useLaggingConversations(tenantSlug ?? '');
  const laggingCount = lagging.data?.count ?? 0;

  // Role-aware top-level nav. Phase 1C ports replace the page stubs.
  const customerNav: NavItem[] = tenantSlug ? [
    { to: `/c/${tenantSlug}`,                label: 'Overview',          icon: OverviewIcon,    end: true },
    { to: `/c/${tenantSlug}/summary`,        label: 'Executive Summary', icon: SummaryIcon },
    { to: `/c/${tenantSlug}/kpis`,           label: 'KPIs',              icon: KPIIcon },
    { to: `/c/${tenantSlug}/financial`,      label: 'Financial Model',   icon: FinancialIcon },
    { to: `/c/${tenantSlug}/analytics`,      label: 'Analytics',         icon: AnalyticsIcon,   badge: laggingCount },
  ] : [];

  const lcaefEnabled = useFeatureFlagBoolean('lcaef_enabled');
  // CHAP-593 - the Discovery sender console is for admins; the server also
  // requires DISCOVERY_SENDERS, so the row is a convenience, not the gate.
  const canSendDiscovery = roles.allowedRoles.includes('admin');

  const customerTools: NavItem[] = tenantSlug ? [
    { to: `/c/${tenantSlug}/voting`,         label: 'Voting',                icon: VotingIcon },
    { to: `/c/${tenantSlug}/okrs`,           label: 'OKRs',                  icon: OKRIcon },
    { to: `/c/${tenantSlug}/renderings`,     label: 'Renderings',            icon: RenderingsIcon },
    { to: `/c/${tenantSlug}/document-lineage`, label: 'Version History',   icon: VersionHistoryIcon },
    // CHAP-491 — LCAEF is one chapter's Foundation contract, not a product
    // feature, so the row appears only for a tenant whose flag is on. The hook
    // returns false while loading, so the row never flashes in and out.
    ...(lcaefEnabled
      ? [{ to: `/c/${tenantSlug}/lcaef`, label: 'LCAEF Campaign', icon: LcaefIcon }]
      : []),
    { to: `/c/${tenantSlug}/alumni`,         label: 'Alumni Reconciliation', icon: AlumniIcon },
    { to: `/c/${tenantSlug}/pipeline`,       label: 'Donor Pipeline',        icon: KPIIcon },
    { to: `/c/${tenantSlug}/participate-leads`, label: 'Participate Leads',  icon: AlumniIcon },
    ...(canSendDiscovery
      ? [{ to: `/c/${tenantSlug}/discovery`, label: 'Discovery', icon: DiscoveryIcon }]
      : []),
    { to: `/c/${tenantSlug}/cabinet`,        label: 'Cabinet',               icon: AlumniIcon },
    { to: `/c/${tenantSlug}/analytics/scenarios`, label: 'Capital Structure', icon: EntityIcon },
    // Parking (CHAP-88) — optional; shown only when the tenant has the
    // parking_enabled flag (tenant-config sets `parking` non-null).
    ...(tenant?.parking ? [{ to: `/c/${tenantSlug}/analytics/parking`, label: 'Parking', icon: AnalyticsIcon }] : []),
    { to: `/c/${tenantSlug}/meetings`,       label: 'Meetings',              icon: KPIIcon },
    { to: `/c/${tenantSlug}/dataroom`,       label: 'Data Room',             icon: DataRoomIcon },
    { to: `/c/${tenantSlug}/feedback`,       label: 'Feedback',              icon: FeedbackIcon },
  ] : [];

  const adminNav: NavItem[] = [
    { to: '/admin',                         label: 'Cross-tenant Dashboard', icon: OverviewIcon, end: true },
    { to: '/admin/tenants',                 label: 'Tenants',                icon: EntityIcon },
    { to: '/admin/users',                   label: 'Users',                  icon: AlumniIcon },
    { to: '/admin/audit-events',            label: 'Audit log',              icon: KPIIcon },
    { to: '/admin/health',                  label: 'System health',          icon: AnalyticsIcon },
    { to: '/admin/billing',                 label: 'Billing health',         icon: KPIIcon },
    { to: '/admin/feature-flags',           label: 'Feature flags',          icon: VotingIcon },
    { to: '/admin/data-room/queue',         label: 'Curation queue',         icon: DataRoomIcon },
  ];

  const useAdminNav = activeRole === 'admin' && pathname.startsWith('/admin');
  const showCustomerNav = !useAdminNav && tenantSlug;

  const handleRoleChange = (newRole: Role) => {
    setStoredRole(newRole, roles.allowedRoles);
    // Navigate to the new role's home
    if (newRole === 'admin') {
      navigate('/admin');
    } else if (newRole === 'team' && tenantSlug) {
      navigate(`/c/${tenantSlug}`);
    } else if (tenantSlug) {
      navigate(`/c/${tenantSlug}`);
    } else {
      navigate('/');
    }
    onNavigate?.();
  };

  // Branding from tenant config (display name + address; logo text derived from initials)
  const tenantDisplayName = tenant?.tenant.displayName ?? 'ChapterBuild';
  const tenantAddress = tenant?.tenant.address ?? '';
  const tenantLogoText = deriveLogoText(tenantDisplayName);

  return (
    <div className="flex flex-col h-full min-w-0" style={{ backgroundColor: '#03244d' }}>
      {/* Branding */}
      <div className="p-4 sm:p-5 border-b border-[#0d3868]">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center border border-white/20 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #ffffff, #b8d4f0)' }}
          >
            <span className="text-orange font-bold text-base" style={{ fontFamily: 'Georgia, serif' }}>
              {tenantLogoText}
            </span>
          </div>
          <div className="min-w-0">
            <h1 className="text-orange text-lg font-bold tracking-tight truncate">{tenantDisplayName}</h1>
            {tenantAddress && (
              <p className="text-neutral text-xs mt-0.5 truncate">{tenantAddress}</p>
            )}
          </div>
        </div>
      </div>

      {/* RoleSwitcher - per role-pattern.md, sits at top of sidebar */}
      <div className="px-2 py-2 border-b border-[#0d3868]">
        <RoleSwitcher pathname={pathname} onRoleChange={handleRoleChange} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
        {useAdminNav && adminNav.map((item) => (
          <NavLinkItem key={item.to} item={item} onClick={onNavigate} />
        ))}

        {showCustomerNav && (
          <>
            {customerNav.map((item) => (
              <NavLinkItem key={item.to} item={item} onClick={onNavigate} />
            ))}

            <div className="pt-3 mt-2">
              <p className="text-neutral/50 text-[10px] uppercase tracking-widest px-3 mb-1">Tools</p>
            </div>
            {customerTools.map((item) => (
              <NavLinkItem key={item.to} item={item} onClick={onNavigate} size="sm" />
            ))}
          </>
        )}

        {!useAdminNav && !showCustomerNav && (
          <p className="text-neutral/60 text-xs px-3 py-2 italic">No tenant selected.</p>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-[#0d3868] space-y-2">
        <button
          type="button"
          onClick={cycleTheme}
          aria-label={`Theme: ${theme}. Click to change.`}
          title="Cycle theme: light → dark → system"
          className="w-full text-left text-xs text-neutral hover:text-white flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white/5"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            {theme === 'dark' ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            ) : theme === 'light' ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
            )}
          </svg>
          <span className="capitalize">{theme}</span> theme
        </button>
        <button
          type="button"
          onClick={signOut}
          className="w-full text-left text-xs text-neutral hover:text-white flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white/5"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
          </svg>
          Sign out
        </button>
        <p className="text-[10px] text-neutral/60 px-2">ChapterBuild · v0.1</p>
      </div>
    </div>
  );
}

function NavLinkItem({ item, onClick, size = 'md' }: { item: NavItem; onClick?: () => void; size?: 'md' | 'sm' }) {
  const Icon = item.icon;
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        `w-full flex items-center gap-3 px-3 py-2 rounded-lg ${textSize} font-medium transition-colors min-w-0 ${
          isActive
            ? 'bg-[#0d3868] text-orange'
            : 'text-neutral hover:bg-[#0d3868] hover:text-white'
        }`
      }
    >
      <Icon />
      <span className="truncate">{item.label}</span>
      {item.badge != null && item.badge > 0 && (
        <span
          className="ml-auto flex-shrink-0 bg-orange text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center"
          title={`${item.badge} lagging conversations`}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Derive a 2-char logo-text from the tenant's display name.
 *
 * Stopgap until tenants have a real logoText / logoUrl field (Phase 1K
 * brand-spice work). Examples:
 *   "Omega Club"     → "OC"   (first letter of two words)
 *   "ChapterBuild"   → "CB"   (capital-letter pairs)
 *   "Auburn LXA"     → "AL"
 *   "Sample"         → "SA"   (first 2 letters)
 */
function deriveLogoText(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return 'CB';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  // Single-word: first 2 capital letters, or first 2 chars
  const capitals = trimmed.match(/[A-Z]/g);
  if (capitals && capitals.length >= 2) {
    return (capitals[0]! + capitals[1]!).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

// --- Icon set (SVG line-icons, no emoji) ---
function OverviewIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}
function SummaryIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}
function KPIIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
}
function FinancialIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  );
}
function AnalyticsIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h.75a2.25 2.25 0 012.25 2.25v3a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25v-2.25zM9.75 6V4.5A2.25 2.25 0 0112 2.25h.75a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H12A2.25 2.25 0 019.75 18V6zM17.25 9V8.25a2.25 2.25 0 012.25-2.25h.75a2.25 2.25 0 012.25 2.25v9.75a2.25 2.25 0 01-2.25 2.25h-.75a2.25 2.25 0 01-2.25-2.25V9z" />
    </svg>
  );
}
function VotingIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function OKRIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="5.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function LcaefIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
      />
    </svg>
  );
}

function VersionHistoryIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function RenderingsIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );
}
function AlumniIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}
function DataRoomIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  );
}
function DiscoveryIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}
function FeedbackIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  );
}
function EntityIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
    </svg>
  );
}
